import { isNativeAddress } from '@spritz-finance/sdk'
import { BigNumber, Contract } from 'ethers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWeb3Connection } from '../Web3Provider'
import { ERC20Interface } from './interfaces'

export default function useTokenAllowance(
    tokenAddress: string | undefined,
    spender?: string,
    owner?: string
) {
    const validErc20Token = !!tokenAddress && !isNativeAddress(tokenAddress) && spender
    const { provider, account } = useWeb3Connection()
    const tokenHolder = owner ?? account

    const [allowance, setAllowance] = useState<BigNumber | undefined>(undefined)
    const prevAllowanceRef = useRef<BigNumber | undefined>(undefined)

    const tokenContract = useMemo(
        () => (validErc20Token ? new Contract(tokenAddress, ERC20Interface, provider) : null),
        [provider, tokenAddress, validErc20Token]
    )

    const fetchAllowance = useCallback(async () => {
        if (!tokenContract || !tokenHolder) return

        const rawAllowance = await tokenContract.allowance(tokenHolder, spender)

        // Only change balance state if it differs from previous one
        if (rawAllowance !== prevAllowanceRef.current) {
            prevAllowanceRef.current = rawAllowance
            setAllowance(rawAllowance)
        }
    }, [tokenContract, tokenHolder, spender])

    useEffect(() => {
        if (!tokenContract) return
        fetchAllowance()
    }, [fetchAllowance, tokenContract])

    useEffect(() => {
        if (!tokenContract) return
        // Fetch user balance on each block
        provider?.on('block', fetchAllowance)

        // Cleanup function is used to unsubscribe from 'block' event and prevent
        // a possible memory leak
        return () => {
            provider?.off('block', fetchAllowance)
        }
    }, [fetchAllowance, provider, tokenAddress, tokenContract, validErc20Token])

    return {
        allowance,
        refetchAllowance: fetchAllowance,
        setAllowance,
    }
}
